/**
 * Centralized third-party script manager.
 *
 * All external scripts (analytics, session replay, chat, A/B testing) are
 * loaded through this module — never injected ad-hoc in components or index.html.
 *
 * Responsibilities:
 *  - Async loading after app mount (never blocks first paint)
 *  - Environment gating (don't load analytics in dev/test)
 *  - Consent-based activation (GDPR — wait for user consent before loading)
 *  - Error isolation (a broken third-party script must never crash the app)
 *
 * @spec SPEC:ENGINEERING:FRONTEND:THIRD-PARTY-SCRIPTS
 *
 * Usage:
 *   import { scriptManager } from '@/services/scripts'
 *   // Call once after app mounts (in main.tsx or a top-level effect)
 *   scriptManager.init()
 *   // After user grants cookie consent:
 *   scriptManager.onConsent()
 */
import { isProduction } from '@/env'
import { logger } from '@/services/logger'

interface Script {
  name: string
  /** Whether this script requires explicit user consent before loading */
  requiresConsent: boolean
  load: () => Promise<void>
}

function loadScriptTag(src: string, attributes?: Record<string, string>): Promise<void> {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) {
      resolve() // Already loaded
      return
    }
    const script = document.createElement('script')
    script.src = src
    script.async = true
    Object.entries(attributes ?? {}).forEach(([k, v]) => script.setAttribute(k, v))
    script.onload = () => resolve()
    script.onerror = () => reject(new Error(`Failed to load script: ${src}`))
    document.head.appendChild(script)
  })
}

// ── Script definitions ────────────────────────────────────────────────────────
// Add scripts here. Set requiresConsent: true for GDPR-sensitive scripts.

const scripts: Script[] = [
  // Example: PostHog analytics (requires consent in GDPR regions)
  // {
  //   name: 'posthog',
  //   requiresConsent: true,
  //   async load() {
  //     await loadScriptTag('https://app.posthog.com/...')
  //     window.posthog?.init(env.VITE_POSTHOG_KEY, { api_host: env.VITE_POSTHOG_HOST })
  //   },
  // },

  // Example: Intercom (requires consent)
  // {
  //   name: 'intercom',
  //   requiresConsent: true,
  //   async load() {
  //     // Intercom inline script...
  //   },
  // },
]

// ── Manager ───────────────────────────────────────────────────────────────────

const loaded = new Set<string>()

async function loadScript(script: Script) {
  if (loaded.has(script.name)) return
  try {
    await script.load()
    loaded.add(script.name)
    logger.debug(`[scripts] loaded "${script.name}"`)
  } catch (err) {
    // Isolated — a broken third-party script must never crash the app
    logger.error(`[scripts] failed to load "${script.name}"`, err)
  }
}

export const scriptManager = {
  /**
   * Initialize non-consent scripts.
   * Call once after app mounts (e.g. in main.tsx after ReactDOM.render).
   */
  async init() {
    if (!isProduction) {
      logger.debug('[scripts] skipping third-party scripts outside production')
      return
    }
    const noConsentRequired = scripts.filter((s) => !s.requiresConsent)
    await Promise.all(noConsentRequired.map(loadScript))
  },

  /**
   * Load consent-gated scripts after the user accepts cookies.
   * Wire this to your cookie consent banner's accept callback.
   */
  async onConsent() {
    if (!isProduction) return
    const consentRequired = scripts.filter((s) => s.requiresConsent)
    await Promise.all(consentRequired.map(loadScript))
  },
}

// Export for testing — allows inspection of the loaded set
export const _loadedScripts = loaded

// Suppress unused import warning — loadScriptTag is used in commented examples
void loadScriptTag
